import { useState, useEffect, useCallback } from 'react';
import { Link, router } from '@inertiajs/react';
import { DndContext, closestCenter, pointerWithin, PointerSensor, useSensor, useSensors, useDroppable, DragOverlay } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import TeacherShell, { TeacherPageHeader } from '../../../components/layout/TeacherShell';
import { Card, Button } from '../../../components/ui';
import CompactUnitView from '../../../components/teacher/CompactUnitView';
import { cn } from '../../../lib/cn';
import HomeworkBuilderPanel from '../../../components/homework/HomeworkBuilderPanel';

// ── Activity Type Icons (matching sidebar style) ──────────

function BookOpenIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 7v14" />
      <path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z" />
    </svg>
  );
}

function LiveSessionIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <rect width="20" height="14" x="2" y="3" rx="2" />
      <path d="M15.033 9.44a.647.647 0 0 1 0 1.12l-4.065 2.352a.645.645 0 0 1-.968-.56V7.648a.645.645 0 0 1 .967-.56z" />
      <path d="M7 21h10" />
    </svg>
  );
}

function EssayIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <rect width="18" height="18" x="3" y="3" rx="2" />
      <path d="M12 12H9.5a2.5 2.5 0 0 1 0-5H17" />
      <path d="M12 7v10" />
      <path d="M16 7v10" />
    </svg>
  );
}

function AssessmentIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <path d="m9 14 2 2 4-4" />
    </svg>
  );
}

function VocabPracticeIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <rect x="8" y="18" width="8" height="4" rx="1" />
    </svg>
  );
}

function PresentationIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 3h20" />
      <path d="M21 3v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V3" />
      <path d="m7 21 5-5 5 5" />
    </svg>
  );
}

function ActivityGeneratorIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z" />
      <path d="m10 8-3 3 3 3" />
      <path d="m14 14 3-3-3-3" />
    </svg>
  );
}

// ── Config ─────────────────────────────────────────────────

const ACTIVITY_TYPES = [
  { id: 'reading',        label: 'Reading',        Icon: BookOpenIcon },
  { id: 'live_session',   label: 'Live Session',   Icon: LiveSessionIcon },
  { id: 'essay',          label: 'Essay',          Icon: EssayIcon },
  { id: 'assessment',     label: 'Assessment',     Icon: AssessmentIcon },
  { id: 'vocab_practice', label: 'Vocab Practice', Icon: VocabPracticeIcon },
  { id: 'presentation',   label: 'Presentation',   Icon: PresentationIcon },
  { id: 'activity',       label: 'Activity',       Icon: ActivityGeneratorIcon },
];

const ACTIVITY_TYPE_LABELS = Object.fromEntries(ACTIVITY_TYPES.map(t => [t.id, t.label]));
const ACTIVITY_TYPE_ICONS = Object.fromEntries(ACTIVITY_TYPES.map(t => [t.id, t.Icon]));

const STATUS_COLORS = {
  draft: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  planned: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  ready: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  completed: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
  archived: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
};

const ACTIVITY_STATUS_DOT = {
  draft: 'bg-yellow-400',
  ready: 'bg-blue-400',
  active: 'bg-green-400',
  archived: 'bg-gray-400',
};

// ── Main Component ────────────────────────────────────────

export default function LessonsShow({ course, unit, unit_context, lesson, prior_homework, main_flow, homework, chapters, incoming_chapters, incoming_questions, incoming_homework_activities, outgoing_chapters, prev_lesson, next_lesson, standalone, available_courses, available_activity_types, available_assessments, homework_items, homework_questions, question_bank_count, course_activities }) {
  const isStandalone = standalone || !course;
  const [pickerSlot, setPickerSlot] = useState(null);
  const [orderedMainFlow, setOrderedMainFlow] = useState(main_flow || []);

  useEffect(() => { setOrderedMainFlow(main_flow || []); }, [main_flow]);

  const lessonActivitiesPath = isStandalone
    ? null
    : `/teacher/courses/${course.id}/units/${unit.id}/lessons/${lesson.id}/lesson_activities`;

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const [activeDragId, setActiveDragId] = useState(null);

  // Prioritize bookend droppable zones, fall back to closestCenter for sortable reorder
  const collisionDetection = useCallback((args) => {
    const pointerCollisions = pointerWithin(args);
    const bookend = pointerCollisions.find(c => String(c.id).startsWith('drop-'));
    if (bookend) return [bookend];
    return closestCenter(args);
  }, []);

  const handleDragStart = useCallback((event) => {
    setActiveDragId(event.active.id);
  }, []);

  const handleDragEnd = useCallback((event) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over || !lessonActivitiesPath) return;

    const overId = String(over.id);

    // Case 1: Dropped on a bookend column
    if (overId.startsWith('drop-')) {
      const targetSlot = overId.replace('drop-', '');
      const draggedId = active.id;
      const newMainFlow = orderedMainFlow.filter(la => la.id !== draggedId);
      setOrderedMainFlow(newMainFlow);

      const activities = [
        ...newMainFlow.map((la, i) => ({ id: la.id, position: i, slot: 'main' })),
        { id: draggedId, position: 999, slot: targetSlot },
      ];

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      fetch(`${lessonActivitiesPath}/reorder`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ activities }),
      }).then(() => router.reload());
      return;
    }

    // Case 2: Reorder within main flow
    if (active.id === over.id) return;
    const oldIndex = orderedMainFlow.findIndex(la => la.id === active.id);
    const newIndex = orderedMainFlow.findIndex(la => la.id === over.id);
    const reordered = arrayMove(orderedMainFlow, oldIndex, newIndex);
    setOrderedMainFlow(reordered);
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    fetch(`${lessonActivitiesPath}/reorder`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ activities: reordered.map((la, i) => ({ id: la.id, position: i, slot: 'main' })) }),
    });
  }, [orderedMainFlow, lessonActivitiesPath]);

  const scrollTitle = isStandalone
    ? (<><Link href="/teacher/dashboard" className="hover:text-blue-600 dark:hover:text-blue-400">Standalone</Link>{'  ›  '}{lesson.title}</>)
    : (<><Link href={`/teacher/courses/${course.id}`} className="hover:text-blue-600 dark:hover:text-blue-400">{course.title}</Link>{'  ›  '}{lesson.title}</>);

  const backLink = isStandalone ? '/teacher/dashboard' : `/teacher/courses/${course.id}`;
  const backLabel = isStandalone ? 'Back to dashboard' : `Back to ${course.title}`;

  const editLink = isStandalone
    ? null
    : `/teacher/courses/${course.id}/units/${unit.id}/lessons/${lesson.id}/edit`;

  const lessonPath = isStandalone
    ? null
    : `/teacher/courses/${course.id}/units/${unit.id}/lessons/${lesson.id}`;

  const activityHref = (activityId) =>
    `/teacher/activities/${activityId}/edit${lessonPath ? `?from_lesson=${encodeURIComponent(lessonPath)}` : ''}`;

  const handleAddActivity = (activityType, slot, opts = {}) => {
    if (!lessonActivitiesPath) return;
    const body = { activity_type: activityType, slot };
    if (opts.chapter_ids) body.chapter_ids = opts.chapter_ids;
    router.post(lessonActivitiesPath, body);
  };

  const handleAddAssessment = (assessmentId, slot) => {
    if (!lessonActivitiesPath) return;
    router.post(lessonActivitiesPath, {
      assessment_id: assessmentId,
      slot: slot,
    });
  };

  const handleRemoveActivity = (la) => {
    if (!lessonActivitiesPath) return;
    const message = la.slottable_type === 'Assessment'
      ? `Remove "${la.title}" from this lesson? The assessment itself won't be deleted.`
      : `Remove "${la.title}" from this lesson? This will permanently delete the activity and its questions.`;
    if (confirm(message)) {
      router.delete(`${lessonActivitiesPath}/${la.id}`);
    }
  };

  const [hwToast, setHwToast] = useState(null);

  // Find the homework lesson_activity ID for adding homework items
  const homeworkLessonActivity = (homework || [])[0];
  const homeworkActivityId = homeworkLessonActivity?.id;

  const handleAssignAsHomework = async (activityId) => {
    if (!homeworkActivityId) return;
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    const res = await fetch(`/teacher/lesson_activities/${homeworkActivityId}/homework_items`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ sourceable_type: 'Activity', sourceable_id: activityId }),
    });
    if (res.ok) {
      setHwToast('Added to homework');
      setTimeout(() => setHwToast(null), 2000);
      router.reload();
    }
  };

  const allActivities = [...(prior_homework || []), ...(main_flow || []), ...(homework || [])];

  return (
    <TeacherShell scrollTitle={scrollTitle}>
      {/* 1. Back link */}
      <Link
        href={backLink}
        className="inline-flex items-center text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white mb-4 transition-colors duration-200"
      >
        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        {backLabel}
      </Link>

      {/* 2. Compact unit navigation */}
      {!isStandalone && (
        <CompactUnitView unitContext={unit_context} />
      )}

      {/* 3. Title, description, actions */}
      <TeacherPageHeader
        title={lesson.title}
        subtitle={lesson.description || 'No description'}
        actions={
          <div className="flex items-center gap-3">
            {editLink && (
              <Link href={editLink}>
                <Button variant="secondary" size="sm">Edit Lesson</Button>
              </Link>
            )}
            <Button variant="primary">Go Live</Button>
          </div>
        }
      />

      {/* Standalone Banner */}
      {isStandalone && (
        <AssignToCourseCard lessonId={lesson.id} courses={available_courses || []} />
      )}

      {/* Lesson Meta */}
      <div className="flex items-center gap-4 mb-8">
        <span className={cn('text-xs font-medium px-2 py-0.5 rounded', STATUS_COLORS[lesson.status] || STATUS_COLORS.draft)}>
          {lesson.status}
        </span>
        <span className="text-sm text-gray-500 dark:text-gray-400 capitalize">
          {lesson.lesson_type.replace('_', ' ')}
        </span>
        {lesson.duration_minutes && (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {lesson.duration_minutes} minutes
          </span>
        )}
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {allActivities.length} {allActivities.length === 1 ? 'activity' : 'activities'}
        </span>
        {unit?.book_title && unit?.library_item_id && (
          <a
            href={`/teacher/library/${unit.library_item_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            Book: {unit.book_title}
          </a>
        )}
      </div>

      {/* ── Lesson Flow ── */}
      <Card className="p-6 mb-8">
        <style>{`
          @keyframes pulse-glow {
            0%, 100% {
              box-shadow: 0 0 15px rgba(59, 130, 246, 0.2), 0 0 30px rgba(59, 130, 246, 0.1);
            }
            50% {
              box-shadow: 0 0 25px rgba(59, 130, 246, 0.35), 0 0 50px rgba(59, 130, 246, 0.15);
            }
          }
        `}</style>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Lesson Flow</h3>
        <DndContext sensors={sensors} collisionDetection={collisionDetection} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="flex items-stretch gap-0 overflow-x-auto pb-2">
          {/* Prior Homework Bookend */}
          <BookendColumn
            title="Prior HW"
            droppableId="drop-prior_homework"
            chapters={incoming_chapters}
            questionCount={(incoming_questions || []).length}
            incomingActivities={incoming_homework_activities || []}
            activities={prior_homework || []}
            activityHref={activityHref}
            onRemove={!isStandalone ? handleRemoveActivity : null}
            onAdd={!isStandalone ? () => setPickerSlot(pickerSlot === 'prior_homework' ? null : 'prior_homework') : null}
            isPickerOpen={pickerSlot === 'prior_homework'}
            availableTypes={available_activity_types}
            availableAssessments={available_assessments}
            onPickerSelect={(type) => { handleAddActivity(type, 'prior_homework'); setPickerSlot(null); }}
            onPickerSelectAssessment={(id) => { handleAddAssessment(id, 'prior_homework'); setPickerSlot(null); }}
            onPickerCancel={() => setPickerSlot(null)}
          />

          <FlowArrow />

          {/* Main flow cards — horizontal, draggable */}
            <SortableContext items={orderedMainFlow.map(la => la.id)} strategy={horizontalListSortingStrategy}>
              {orderedMainFlow.map((la) => (
                <SortableFlowSlotCard
                  key={la.id}
                  la={la}
                  activityHref={activityHref}
                  onRemove={!isStandalone ? () => handleRemoveActivity(la) : null}
                  onAssignHomework={!isStandalone && homeworkActivityId && la.slottable_type === 'Activity' ? () => handleAssignAsHomework(la.activity_id) : null}
                />
              ))}
            </SortableContext>

          {/* [+] Inline Adder — always last before Homework */}
          {!isStandalone && (
            <>
              <InlineAdder
                expanded={pickerSlot === 'main'}
                onExpand={() => setPickerSlot(pickerSlot === 'main' ? null : 'main')}
                onCollapse={() => setPickerSlot(null)}
                availableTypes={available_activity_types}
                availableAssessments={available_assessments}
                onSelect={(type) => { handleAddActivity(type, 'main'); setPickerSlot(null); }}
                onSelectAssessment={(id) => { handleAddAssessment(id, 'main'); setPickerSlot(null); }}
                glow={(main_flow || []).length === 0}
              />
              <FlowArrow />
            </>
          )}

          {/* Homework Bookend */}
          <BookendColumn
            title="Homework"
            droppableId="drop-homework"
            chapters={outgoing_chapters}
            questionCount={(homework_questions || []).length}
            activities={homework || []}
            activityHref={activityHref}
            onRemove={!isStandalone ? handleRemoveActivity : null}
            onAdd={!isStandalone ? () => setPickerSlot(pickerSlot === 'homework' ? null : 'homework') : null}
            isPickerOpen={pickerSlot === 'homework'}
            availableTypes={available_activity_types}
            availableAssessments={available_assessments}
            onPickerSelect={(type) => {
              const chapterIds = (outgoing_chapters || []).map(ch => ch.id);
              handleAddActivity(type, 'homework', chapterIds.length > 0 ? { chapter_ids: chapterIds } : {});
              setPickerSlot(null);
            }}
            onPickerSelectAssessment={(id) => { handleAddAssessment(id, 'homework'); setPickerSlot(null); }}
            onPickerCancel={() => setPickerSlot(null)}
          />
        </div>

        <DragOverlay>
          {activeDragId ? (
            <FlowSlotCard
              la={orderedMainFlow.find(la => la.id === activeDragId)}
              activityHref={activityHref}
              draggable
            />
          ) : null}
        </DragOverlay>
        </DndContext>
      </Card>

      {/* Homework toast */}
      {hwToast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-full bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-medium shadow-lg animate-pulse">
          {hwToast}
        </div>
      )}

      {/* Combined Homework & Reading Materials */}
      {((incoming_chapters || []).length > 0 || (incoming_questions || []).length > 0 || (outgoing_chapters || []).length > 0 || (homework_questions || []).length > 0) && (
        <Card className="p-6 mb-8">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-semibold text-gray-900 dark:text-white">Homework & Reading</h3>
            {!isStandalone && (
              <a
                href={`/teacher/lessons/${lesson.id}/question_bank`}
                className="inline-flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                Question Bank{question_bank_count > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 text-[10px] font-medium">
                    {question_bank_count}
                  </span>
                )}
              </a>
            )}
          </div>
          {unit?.book_title && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              From {unit.library_item_id ? (
                <a href={`/teacher/library/${unit.library_item_id}`} target="_blank" rel="noopener noreferrer" className="font-medium text-blue-600 dark:text-blue-400 hover:underline">{unit.book_title}</a>
              ) : (
                <span className="font-medium text-gray-700 dark:text-gray-300">{unit.book_title}</span>
              )}
              {unit?.book_author && <span> by {unit.book_author}</span>}
            </p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {((incoming_chapters || []).length > 0 || (incoming_questions || []).length > 0) && (
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                  Prior Homework — Students should have completed
                </p>
                {(incoming_chapters || []).length > 0 && (
                  <div className="space-y-2">
                    {incoming_chapters.map((ch) => (
                      <ChapterRow key={ch.id} chapter={ch} />
                    ))}
                  </div>
                )}
                {(incoming_questions || []).length > 0 && (
                  <CollapsibleQuestions
                    label={`Questions (${incoming_questions.length})`}
                    questions={incoming_questions}
                    className={(incoming_chapters || []).length > 0 ? 'mt-3' : ''}
                  />
                )}
              </div>
            )}

            {((outgoing_chapters || []).length > 0 || (homework_questions || []).length > 0) && (
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                  Homework — Reading for next class{next_lesson ? ` (${next_lesson.title})` : ''}
                </p>
                {(outgoing_chapters || []).length > 0 && (
                  <div className="space-y-2">
                    {outgoing_chapters.map((ch) => (
                      <ChapterRow key={ch.id} chapter={ch} />
                    ))}
                  </div>
                )}
                {(homework_questions || []).length > 0 && (
                  <CollapsibleQuestions
                    label={`Questions (${homework_questions.length})`}
                    questions={homework_questions}
                    className={(outgoing_chapters || []).length > 0 ? 'mt-3' : ''}
                  />
                )}
              </div>
            )}
          </div>

          {/* Homework Builder (add more items) */}
          {!isStandalone && homeworkActivityId && (homework_items || []).length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <HomeworkBuilderPanel
                homeworkActivityId={homeworkActivityId}
                items={homework_items}
                courseActivities={course_activities || []}
                lessonId={lesson.id}
              />
            </div>
          )}
        </Card>
      )}
    </TeacherShell>
  );
}

// ── Flow Arrow (horizontal connector) ─────────────────────

function FlowArrow() {
  return (
    <div className="flex items-center px-2 self-center flex-shrink-0">
      <svg className="w-6 h-6 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────

function compactChapterLabel(title) {
  if (!title) return null;
  // Split on ", " and classify each part
  const parts = title.split(', ').map(p => p.trim());
  if (parts.length === 0) return null;

  const chNum = (part) => {
    const m = part.match(/^Chapter\s+(\d+)$/i) || part.match(/^Ch\.\s*(\d+)$/i);
    return m ? m[1] : null;
  };

  const toLabel = (part) => {
    const n = chNum(part);
    if (n) return `Ch. ${n}`;
    return 'Preface';
  };

  const first = toLabel(parts[0]);
  if (parts.length === 1) return first;

  const firstNum = chNum(parts[0]);
  const lastNum = chNum(parts[parts.length - 1]);

  // "Ch. 1 – 5" when both are numbered chapters, "Preface – Ch. 4" otherwise
  if (firstNum && lastNum) return `Ch. ${firstNum} – ${lastNum}`;
  return `${first} – ${toLabel(parts[parts.length - 1])}`;
}

// ── Activity Card ─────────────────────────────────────────

function ActivityCard({ la, activityHref, onRemove, compact }) {
  const isAssessment = la.slottable_type === 'Assessment';
  const statusDot = ACTIVITY_STATUS_DOT[la.status] || ACTIVITY_STATUS_DOT.draft;
  const TypeIcon = isAssessment ? AssessmentIcon : (ACTIVITY_TYPE_ICONS[la.activity_type] || ActivityGeneratorIcon);
  const typeLabel = isAssessment
    ? 'Assessment'
    : (ACTIVITY_TYPE_LABELS[la.activity_type] || 'Activity');

  const href = isAssessment
    ? `/teacher/assessments/${la.assessment_id}`
    : activityHref(la.activity_id);

  return (
    <div className={cn(
      'flex items-center justify-between rounded-lg border bg-white dark:bg-gray-900',
      'border-gray-200 dark:border-gray-700',
      compact ? 'p-2.5' : 'p-4'
    )}>
      <div className="flex items-center gap-2 min-w-0">
        <TypeIcon className={cn(
          'flex-shrink-0',
          'text-gray-500 dark:text-gray-400',
          compact ? 'w-4 h-4' : 'w-5 h-5'
        )} />
        <div className="min-w-0">
          <Link
            href={href}
            className={cn(
              'font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 truncate block',
              compact ? 'text-xs' : 'text-sm'
            )}
          >
            {compact ? (compactChapterLabel(la.title) || la.title) : la.title}
          </Link>
          <div className="flex items-center gap-2 text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 whitespace-nowrap">
            <span className="flex items-center gap-1">
              <span className={cn('w-1.5 h-1.5 rounded-full', statusDot)} />
              {la.status}
            </span>
            {!compact && <span className="capitalize">{typeLabel}</span>}
            {la.question_count > 0 && <span>{la.question_count}Q</span>}
          </div>
        </div>
      </div>
      {onRemove && (
        <button
          onClick={() => onRemove(la)}
          className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex-shrink-0"
          title={isAssessment ? "Remove from lesson" : "Remove activity"}
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

// ── Flow Slot Card (horizontal card for main flow items) ──

function FlowSlotCard({ la, activityHref, onRemove, draggable, onAssignHomework }) {
  const isAssessment = la.slottable_type === 'Assessment';
  const statusDot = ACTIVITY_STATUS_DOT[la.status] || ACTIVITY_STATUS_DOT.draft;
  const TypeIcon = isAssessment ? AssessmentIcon : (ACTIVITY_TYPE_ICONS[la.activity_type] || ActivityGeneratorIcon);
  const typeLabel = isAssessment
    ? 'Assessment'
    : (ACTIVITY_TYPE_LABELS[la.activity_type] || 'Activity');

  const href = isAssessment
    ? `/teacher/assessments/${la.assessment_id}`
    : activityHref(la.activity_id);

  return (
    <div className={cn(
      'w-40 flex-shrink-0 rounded-lg border-2 p-3 flex flex-col self-stretch',
      'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900',
      draggable && 'cursor-grab active:cursor-grabbing'
    )}>
      <div className="flex items-center justify-between mb-1">
        <TypeIcon className={cn(
          'w-4 h-4 flex-shrink-0',
          'text-gray-400 dark:text-gray-500'
        )} />
        <div className="flex items-center gap-0.5">
          {onAssignHomework && (
            <button
              onClick={(e) => { e.stopPropagation(); onAssignHomework(); }}
              className="p-0.5 rounded text-gray-300 hover:text-gray-900 dark:text-gray-600 dark:hover:text-white transition-colors"
              title="Assign as homework"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </button>
          )}
          {onRemove && (
            <button
              onClick={() => onRemove(la)}
              className="p-0.5 rounded text-gray-300 hover:text-red-500 dark:text-gray-600 dark:hover:text-red-400 transition-colors"
              title={isAssessment ? 'Remove from lesson' : 'Remove activity'}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>
      <Link
        href={href}
        className="text-xs font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 truncate block"
      >
        {la.title}
      </Link>
      <p className="text-[10px] text-gray-500 dark:text-gray-400 capitalize mt-0.5 truncate">{typeLabel}</p>
      <div className="flex items-center gap-1.5 text-[10px] text-gray-400 dark:text-gray-500 mt-auto pt-1">
        <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', statusDot)} />
        <span>{la.status}</span>
        {la.question_count > 0 && <span>· {la.question_count} Q</span>}
      </div>
    </div>
  );
}

// ── Sortable Flow Slot Card (drag-and-drop wrapper) ──────

function SortableFlowSlotCard({ la, activityHref, onRemove, onAssignHomework }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: la.id });
  const style = {
    transform: transform ? `translate3d(${Math.round(transform.x)}px, 0, 0)` : undefined,
    transition,
    zIndex: isDragging ? 10 : undefined,
    position: 'relative',
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-stretch self-stretch" {...attributes} {...listeners}>
      <div className={cn('flex', isDragging && 'opacity-50 shadow-lg rounded-lg')}>
        <FlowSlotCard la={la} activityHref={activityHref} onRemove={onRemove} onAssignHomework={onAssignHomework} draggable />
      </div>
      <FlowArrow />
    </div>
  );
}

// ── Inline Adder ([+] card in the flow) ──────────────────

function InlineAdder({ expanded, onExpand, onCollapse, availableTypes, availableAssessments, onSelect, onSelectAssessment, glow }) {
  const [showAssessments, setShowAssessments] = useState(false);
  const types = ACTIVITY_TYPES.filter(t => (availableTypes || []).includes(t.id));
  const assessments = availableAssessments || [];

  const handleTypeClick = (typeId) => {
    if (typeId === 'assessment') {
      setShowAssessments(true);
      return;
    }
    onSelect(typeId);
  };

  const handleClose = () => {
    setShowAssessments(false);
    onCollapse();
  };

  // Expanded: assessment list
  if (expanded && showAssessments) {
    return (
      <div className="flex-shrink-0 rounded-lg border-2 border-dashed border-blue-300 dark:border-blue-600 bg-blue-50/50 dark:bg-blue-900/10 p-3 self-stretch min-w-[14rem]">
        <div className="flex items-center gap-2 mb-2">
          <button onClick={() => setShowAssessments(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <p className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wider">Pick an Assessment</p>
          <div className="flex-1" />
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {assessments.length === 0 ? (
          <p className="text-[10px] text-gray-400 dark:text-gray-500 text-center py-2">
            No assessments available yet.
          </p>
        ) : (
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {assessments.map(a => (
              <button
                key={a.id}
                onClick={() => { setShowAssessments(false); onSelectAssessment(a.id); }}
                className="w-full flex items-center gap-2 text-left p-2 rounded-md border border-gray-200 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors bg-white dark:bg-gray-900 group"
              >
                <svg className="w-4 h-4 flex-shrink-0 text-blue-400 dark:text-blue-500 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-gray-900 dark:text-white truncate">{a.title}</p>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                    {a.assessment_type.replace(/_/g, ' ')}
                    {a.question_count > 0 && ` · ${a.question_count} items`}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Expanded: type grid
  if (expanded) {
    return (
      <div className="flex-shrink-0 rounded-lg border-2 border-blue-300 dark:border-blue-600 bg-blue-50/50 dark:bg-blue-900/10 p-3 self-stretch">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Add</p>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="grid grid-cols-4 gap-1.5">
          {types.map((type) => (
            <button
              key={type.id}
              onClick={() => handleTypeClick(type.id)}
              className="flex flex-col items-center gap-1 p-2 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-blue-400 dark:hover:border-blue-500 transition-all group"
            >
              <type.Icon className="w-5 h-5 text-gray-400 dark:text-gray-500 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors" />
              <span className="text-[9px] font-medium text-gray-500 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors leading-tight text-center">
                {type.label}
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Collapsed: [+] card
  return (
    <button
      onClick={onExpand}
      className="w-40 flex-shrink-0 rounded-lg border-2 border-dashed border-blue-300 dark:border-blue-600 transition-all hover:border-blue-400 dark:hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 group flex items-center justify-center p-3"
      style={glow ? { animation: 'pulse-glow 2s ease-in-out infinite' } : undefined}
    >
      <div className="flex flex-col items-center gap-1">
        <svg className="w-6 h-6 text-blue-400 dark:text-blue-500 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        <span className="text-[10px] font-medium text-blue-400 dark:text-blue-500 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors">
          Add
        </span>
      </div>
    </button>
  );
}

// ── Bookend Column (Prior HW / Homework) ─────────────────
// Two-column layout: left column for chapter/reading summary, right column for
// activity cards stacked two-tall and growing rightward.

function BookendColumn({ title, droppableId, chapters, questionCount, incomingActivities, activities, activityHref, onRemove, onAdd, isPickerOpen, availableTypes, availableAssessments, onPickerSelect, onPickerSelectAssessment, onPickerCancel }) {
  const { isOver, setNodeRef } = useDroppable({ id: droppableId || title });
  const [showAssessments, setShowAssessments] = useState(false);
  const chapterList = chapters || [];
  const hasContent = activities.length > 0 || chapterList.length > 0;

  const totalWords = chapterList.reduce((sum, ch) => sum + (ch.word_count || 0), 0);
  const readingTime = totalWords > 0 ? Math.ceil(totalWords / 200) : 0;

  const handleTypeClick = (typeId) => {
    if (typeId === 'assessment') {
      setShowAssessments(true);
      return;
    }
    onPickerSelect(typeId);
  };

  const handleCancel = () => {
    setShowAssessments(false);
    onPickerCancel();
  };

  const types = ACTIVITY_TYPES.filter(t => (availableTypes || []).includes(t.id));
  const assessments = availableAssessments || [];

  const allCards = [...(incomingActivities || []), ...activities];
  const hasCards = allCards.length > 0;

  return (
    <div ref={setNodeRef} className={cn(
      'flex-shrink-0 rounded-lg border-2 p-3 transition-all',
      isOver
        ? 'border-blue-500 dark:border-blue-400 ring-2 ring-blue-500/30 dark:ring-blue-400/30 bg-blue-50/50 dark:bg-blue-900/10'
        : hasContent
          ? 'border-gray-300 dark:border-gray-600'
          : 'border-dashed border-gray-200 dark:border-gray-700'
    )}>
      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 text-center">{title}</p>

      <div className={cn('flex gap-3', hasCards ? 'items-start' : 'items-center justify-center')}>
        {/* Left column: chapter/reading summary */}
        <div className="w-24 flex-shrink-0 text-center">
          {chapterList.length > 0 ? (
            <>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {chapterList.length} <span className="text-xs font-normal text-gray-500 dark:text-gray-400">{chapterList.length === 1 ? 'chapter' : 'chapters'}</span>
              </p>
              {readingTime > 0 && (
                <p className="text-[10px] text-gray-400 dark:text-gray-500">~{readingTime} min reading</p>
              )}
            </>
          ) : (
            <p className="text-[10px] text-gray-400 dark:text-gray-500 italic">No chapters</p>
          )}
        </div>

        {/* Right column: activity cards in a 2-row grid */}
        {hasCards && (
          <div className={cn(
            'grid grid-flow-col gap-1.5 auto-cols-[9.5rem]',
            allCards.length > 1 ? 'grid-rows-2' : 'grid-rows-1'
          )}>
            {(incomingActivities || []).map((la) => (
              <ActivityCard
                key={`inc-${la.id}`}
                la={la}
                activityHref={activityHref}
                compact
              />
            ))}
            {activities.map((la) => (
              <ActivityCard
                key={la.id}
                la={la}
                activityHref={activityHref}
                onRemove={onRemove ? () => onRemove(la) : null}
                compact
              />
            ))}
          </div>
        )}
      </div>

      {/* Picker / Add button */}
      <div className="mt-2">
        {/* Picker: assessment list view */}
        {isPickerOpen && showAssessments ? (
          <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-2">
            <div className="flex items-center gap-1 mb-2">
              <button onClick={() => setShowAssessments(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Assess.</p>
              <div className="flex-1" />
              <button onClick={handleCancel} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {assessments.length === 0 ? (
              <p className="text-[10px] text-gray-400 text-center py-1">None available</p>
            ) : (
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {assessments.map(a => (
                  <button
                    key={a.id}
                    onClick={() => { setShowAssessments(false); onPickerSelectAssessment(a.id); }}
                    className="w-full text-left p-1.5 rounded border border-gray-200 dark:border-gray-700 hover:border-gray-900 dark:hover:border-white transition-colors bg-white dark:bg-gray-900"
                  >
                    <p className="text-[10px] font-medium text-gray-900 dark:text-white truncate">{a.title}</p>
                    <p className="text-[9px] text-gray-400 truncate">{a.assessment_type.replace(/_/g, ' ')}{a.question_count > 0 && ` · ${a.question_count}Q`}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : isPickerOpen ? (
          /* Picker: type grid view */
          <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-2">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Add</p>
              <button onClick={handleCancel} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="grid grid-cols-3 gap-1">
              {types.map((type) => (
                <button
                  key={type.id}
                  onClick={() => handleTypeClick(type.id)}
                  className="flex flex-col items-center gap-0.5 p-1.5 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-gray-900 dark:hover:border-white transition-all group"
                >
                  <type.Icon className="w-4 h-4 text-gray-400 dark:text-gray-500 group-hover:text-gray-900 dark:group-hover:text-white transition-colors" />
                  <span className="text-[8px] font-medium text-gray-500 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white transition-colors leading-tight text-center">
                    {type.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ) : onAdd && (
          <button
            onClick={onAdd}
            className="w-full py-1 text-[10px] text-gray-400 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors flex items-center justify-center gap-1"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add
          </button>
        )}
      </div>
    </div>
  );
}

// ── Supporting Components ─────────────────────────────────

function ChapterRow({ chapter }) {
  const wordCount = chapter.word_count || 0;
  const readingMin = wordCount > 0 ? Math.ceil(wordCount / 200) : 0;

  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50">
      <div>
        <p className="font-medium text-gray-900 dark:text-white text-sm">
          {chapter.formatted_label || chapter.title}
        </p>
        <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          {wordCount > 0 && <span>{wordCount.toLocaleString()} words</span>}
          {readingMin > 0 && <span>~{readingMin} min</span>}
        </div>
      </div>
    </div>
  );
}

function CollapsibleQuestions({ label, questions, className }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={className}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
      >
        <svg
          className={cn('w-3 h-3 transition-transform duration-200', expanded && 'rotate-90')}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        {label}
      </button>
      {expanded && (
        <div className="space-y-2 mt-2">
          {questions.map((q) => (
            <HomeworkQuestionRow key={q.id} question={q} />
          ))}
        </div>
      )}
    </div>
  );
}

const QUESTION_TYPE_SHORT = {
  multiple_choice: 'MCQ',
  mcq: 'MCQ',
  short_answer: 'Short',
  essay: 'Essay',
  fill_blank: 'Fill',
  table_completion: 'Table',
};

function HomeworkQuestionRow({ question }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50">
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-900 dark:text-white line-clamp-2">{question.prompt}</p>
        <div className="flex items-center gap-2 text-[10px] text-gray-500 dark:text-gray-400 mt-1">
          <span className="font-medium px-1.5 py-0.5 rounded bg-gray-200/60 dark:bg-gray-700">
            {QUESTION_TYPE_SHORT[question.question_type] || question.question_type}
          </span>
          {question.bloom_level && (
            <span className="capitalize">{question.bloom_level}</span>
          )}
          {question.activity_title && (
            <span className="truncate">{question.activity_title}</span>
          )}
          {!question.teacher_approved && (
            <span className="text-yellow-600 dark:text-yellow-400">Draft</span>
          )}
        </div>
      </div>
    </div>
  );
}

function AssignToCourseCard({ lessonId, courses }) {
  const [showPicker, setShowPicker] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const courseUnits = selectedCourse ? courses.find(c => c.id === selectedCourse)?.units || [] : [];

  const handleAssign = () => {
    if (!selectedCourse || !selectedUnit) return;
    setIsSubmitting(true);
    router.patch(`/teacher/standalone_lessons/${lessonId}/assign`, {
      course_id: selectedCourse,
      unit_id: selectedUnit,
    }, {
      onFinish: () => setIsSubmitting(false),
    });
  };

  return (
    <Card className="p-4 mb-8 border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">Standalone lesson</p>
          <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-0.5">
            This lesson isn't part of a course yet.
          </p>
        </div>
        {!showPicker && (
          <Button variant="secondary" size="sm" onClick={() => setShowPicker(true)}>
            Assign to Course
          </Button>
        )}
      </div>

      {showPicker && (
        <div className="mt-4 pt-4 border-t border-yellow-200 dark:border-yellow-800">
          {courses.length === 0 ? (
            <div className="text-sm text-yellow-700 dark:text-yellow-400">
              <p>No courses yet.</p>
              <Link href="/teacher/courses/new" className="underline hover:no-underline mt-1 inline-block">
                Create a course first
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Course</label>
                <select
                  value={selectedCourse || ''}
                  onChange={(e) => { setSelectedCourse(Number(e.target.value) || null); setSelectedUnit(''); }}
                  className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white text-sm"
                >
                  <option value="">Select a course...</option>
                  {courses.map(c => (
                    <option key={c.id} value={c.id}>{c.title}</option>
                  ))}
                </select>
              </div>

              {selectedCourse && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Unit</label>
                  {courseUnits.length === 0 ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      This course has no units.{' '}
                      <Link href={`/teacher/courses/${selectedCourse}/units/new`} className="underline hover:no-underline">
                        Create one
                      </Link>
                    </p>
                  ) : (
                    <select
                      value={selectedUnit}
                      onChange={(e) => setSelectedUnit(Number(e.target.value) || '')}
                      className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white text-sm"
                    >
                      <option value="">Select a unit...</option>
                      {courseUnits.map(u => (
                        <option key={u.id} value={u.id}>{u.title}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              <div className="flex items-center gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!selectedCourse || !selectedUnit || isSubmitting}
                  onClick={handleAssign}
                >
                  {isSubmitting ? 'Assigning...' : 'Assign'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowPicker(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
